/**
 * One versioned channel filter on one upstream connection — the transport
 * half of odds-gamestate-feed-filters D1 + D1a (spec: galactus-dev-workspace
 * `docs/agents/handoffs/odds-gamestate-feed-filters/handoff.md` @ 2245b9f).
 *
 * Shared by the three places that run the `oddspapi` / `gamestate`
 * lifecycle — the SharedWorker, HydraClient's direct socket and the Core
 * game-state transport — so what we send, what we admit and when we leave a
 * socket read one definition. Nothing here touches a socket: the owner hands
 * in `send` and `reconnect`.
 *
 * Per socket:
 *   openSocket()  — a socket boundary. THIS socket's mode is decided here:
 *                   `legacy` when the previous socket expired (D1a step 2 —
 *                   the mark lasts one socket), else `filtered`.
 *   subscribe(f)  — filtered: `{op:'subscribe', channel, filter}` and the ack
 *                   timer. The install streams the scoped snapshot by itself
 *                   (Begin → frames → End), so no `{op:'snapshot'}` follows.
 *                   legacy: the channel's bare subscribe + explicit snapshot,
 *                   with no filter for the socket's whole life (D1a step 3).
 *   update(f)     — a new filter: a replace on the wire, at most one per
 *                   REPLACE_MIN_MS, trailing edge, newest wins, and a flap
 *                   back to what is on the wire sends nothing (the markets
 *                   limiter's rule). Nothing on a legacy socket.
 *   onAck(ack)    — clears the timer and installs the exact pair (A2′). A
 *                   rejected FIRST filter takes the fresh-socket path (D1a
 *                   step 5); a rejected later one keeps the installed pair,
 *                   as the server does (D1).
 *   admits(frame) — filtered: the exact installed pair only, so nothing
 *                   before the ack. legacy: unstamped frames only — a
 *                   stamped one is a protocol violation, dropped and
 *                   counted (D1a step 4).
 *   closeSocket() — the socket went away.
 *
 * Expiry (D1a): no `filterAck` for this channel within ACK_TIMEOUT_MS of a
 * filter-bearing subscribe → the NEXT socket is marked legacy and the owner
 * is asked to reconnect. The owner detaches the socket before closing it (it
 * handles no further message from it), so a late ack or a stamped frame from
 * that socket is never processed: never a same-socket fallback. The cases
 * the timeout cannot tell apart — an engine without the filter, and an
 * installed filter whose ack was lost — both end on a fresh socket, which
 * holds no filter on any channel until it sends one.
 */
import {
  FilterFence,
  channelFilterKey,
  isStampedFrame,
  type ChannelFilterChannel,
  type FeedFilterAggregatorTimers,
  type FenceAck,
  type FenceAckResult,
  type GameStateFilter,
  type OpFilter,
} from './feedFilter';

/** Spec D1a: the ack window, the same 8 s the markets channel uses. */
export const CHANNEL_ACK_TIMEOUT_MS = 8_000;
/** One replace per second at most (the markets limiter's bound). */
export const CHANNEL_REPLACE_MIN_MS = 1_000;

/** `closed` = no socket; `filtered` = this socket sends the filter;
 *  `legacy` = this socket runs the channel bare (D1a, one socket). */
export type ChannelLinkMode = 'closed' | 'filtered' | 'legacy';
export type ChannelLinkExpiry = 'ack-timeout' | 'rejected-first-filter';

export interface ChannelFilterLinkOptions {
  channel: ChannelFilterChannel;
  /** Send one op on the CURRENT socket. */
  send: (msg: Record<string, unknown>) => void;
  /** D1a: detach and close the current socket, then reconnect through the
   *  owner's normal path. The link has already marked the next socket legacy
   *  and closed itself. */
  reconnect: (reason: ChannelLinkExpiry) => void;
  /** The channel's legacy subscribe: the bare subscribe + explicit snapshot. */
  legacyOps: () => Array<Record<string, unknown>>;
  log?: (kind: string, detail?: Record<string, unknown>) => void;
  timers?: FeedFilterAggregatorTimers;
  now?: () => number;
  ackTimeoutMs?: number;
  replaceMinMs?: number;
}

export interface ChannelLinkCounters {
  /** Stamped frames dropped on a legacy socket (D1a step 4). */
  stampedOnLegacy: number;
  /** Frames dropped on a filtered socket: before the ack, or stamped with
   *  any pair but the installed one. */
  unadmitted: number;
  /** Fresh-socket exits (ack timeout or a rejected first filter). */
  expiries: number;
  /** Rejected acks (first or later). */
  rejected: number;
}

export class ChannelFilterLink<F extends OpFilter | GameStateFilter = OpFilter, E = F> {
  readonly fence = new FilterFence<E>();
  mode: ChannelLinkMode = 'closed';
  /** The ack'd `effective` of the installed generation (null before it). */
  installedScope: E | null = null;
  readonly counters: ChannelLinkCounters = { stampedOnLegacy: 0, unadmitted: 0, expiries: 0, rejected: 0 };

  private legacyNext = false;
  private subscribed = false;
  /** Filter-bearing subscribes on this socket still awaiting their ack —
   *  the server answers each one exactly once (installed, same selection or
   *  rejected). */
  private outstanding = 0;
  private ackTimer: unknown = null;
  private wireKey: string | null = null;
  private latest: F | null = null;
  private lastSendMs = 0;
  private pendingReplace: unknown = null;
  /** Bumped at every socket boundary; a timer armed on an older socket
   *  speaks for nobody. */
  private socketEpoch = 0;
  private readonly timers: FeedFilterAggregatorTimers;
  private readonly now: () => number;
  private readonly ackTimeoutMs: number;
  private readonly replaceMinMs: number;

  constructor(private readonly opts: ChannelFilterLinkOptions) {
    this.timers = opts.timers ?? {
      setTimeout: (fn, ms) => setTimeout(fn, ms),
      clearTimeout: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
    };
    this.now = opts.now ?? (() => Date.now());
    this.ackTimeoutMs = opts.ackTimeoutMs ?? CHANNEL_ACK_TIMEOUT_MS;
    this.replaceMinMs = opts.replaceMinMs ?? CHANNEL_REPLACE_MIN_MS;
  }

  get channel(): ChannelFilterChannel { return this.opts.channel; }
  /** The channel is subscribed on the current socket. */
  get isSubscribed(): boolean { return this.subscribed; }
  /** A filter generation is installed on the current (filtered) socket. */
  get installed(): boolean { return this.mode === 'filtered' && this.fence.pair != null; }
  /** Is an `{op:'snapshot'}` meaningful now? On a legacy socket it is the
   *  baseline; on a filtered one it re-streams the installed scope. Before
   *  the ack the install's own burst is already coming. */
  get canRequestSnapshot(): boolean { return this.subscribed && (this.mode === 'legacy' || this.installed); }
  /** The filter most recently handed in (sent or pending). */
  get currentFilter(): F | null { return this.latest; }

  /** A new socket opened (or is about to be subscribed). Returns its mode. */
  openSocket(): ChannelLinkMode {
    this.resetSocket();
    this.mode = this.legacyNext ? 'legacy' : 'filtered';
    if (this.legacyNext) this.opts.log?.('channel-filter.legacy-socket', { channel: this.opts.channel });
    this.legacyNext = false;
    return this.mode;
  }

  /** The socket went away (closed, dropped, torn down by the owner). The
   *  legacy mark for the next socket, if any, survives. */
  closeSocket(): void {
    this.resetSocket();
    this.mode = 'closed';
  }

  /** Subscribe this channel on the current socket, once per socket. */
  subscribe(filter: F): void {
    this.latest = filter;
    if (this.mode === 'closed' || this.subscribed) return;
    this.subscribed = true;
    if (this.mode === 'legacy') {
      for (const op of this.opts.legacyOps()) this.opts.send(op);
      return;
    }
    // The connect subscribe IS a send of this filter; its window is not
    // started, so the first change after a connect goes out at once.
    this.sendFilter(filter);
    this.lastSendMs = 0;
  }

  /** The filter changed: replace it on the wire (rate-limited). Before the
   *  subscribe it only updates what the subscribe will carry. */
  update(filter: F): void {
    this.latest = filter;
    if (this.mode !== 'filtered' || !this.subscribed) return;
    const key = channelFilterKey(filter);
    const now = this.now();
    const due = this.lastSendMs + this.replaceMinMs - now;
    if (due <= 0 && this.pendingReplace == null) {
      if (key === this.wireKey) return; // already on the wire
      this.sendFilter(filter);
      this.lastSendMs = now;
      return;
    }
    if (this.pendingReplace != null) return; // the trailing send reads `latest`
    const epoch = this.socketEpoch;
    this.pendingReplace = this.timers.setTimeout(() => {
      this.pendingReplace = null;
      const latest = this.latest;
      if (epoch !== this.socketEpoch || this.mode !== 'filtered' || !this.subscribed || !latest) return;
      if (channelFilterKey(latest) === this.wireKey) return; // settled back
      this.sendFilter(latest);
      this.lastSendMs = this.now();
    }, Math.max(due, 0));
  }

  /** A `filterAck` for this channel on the current socket. Returns the
   *  fence's verdict, or null when the socket runs legacy (it sent no
   *  filter, so an ack there means nothing). */
  onAck(ack: FenceAck<E>): FenceAckResult<E> | null {
    if (this.mode !== 'filtered') return null;
    this.clearAckTimer();
    this.outstanding = Math.max(0, this.outstanding - 1);
    const res = this.fence.onAck(ack);
    if (res.kind === 'rejected') {
      this.counters.rejected += 1;
      this.opts.log?.('channel-filter.rejected', { channel: this.opts.channel, error: res.error, installed: this.fence.pair != null });
      // A rejected FIRST filter subscribes nothing server-side (generation
      // 0): leave for a fresh socket, the one exit to legacy (D1a step 5).
      if (this.fence.pair == null) { this.expire('rejected-first-filter'); return res; }
    } else if (res.kind === 'installed') {
      this.installedScope = res.effective;
    }
    if (this.outstanding > 0) this.armAckTimer();
    return res;
  }

  /** Admission for one frame of this channel (data, Begin or End). */
  admits(frame: object): boolean {
    if (this.mode === 'legacy') {
      if (isStampedFrame(frame)) { this.counters.stampedOnLegacy += 1; return false; }
      return true;
    }
    if (this.mode === 'filtered' && this.fence.admits(frame as { socketIncarnation?: unknown; filterGeneration?: unknown })) return true;
    this.counters.unadmitted += 1;
    return false;
  }

  private sendFilter(filter: F): void {
    this.wireKey = channelFilterKey(filter);
    this.opts.send({ op: 'subscribe', channel: this.opts.channel, filter });
    this.outstanding += 1;
    if (this.ackTimer == null) this.armAckTimer();
  }

  private armAckTimer(): void {
    const epoch = this.socketEpoch;
    this.clearAckTimer();
    this.ackTimer = this.timers.setTimeout(() => {
      this.ackTimer = null;
      if (epoch !== this.socketEpoch || this.mode !== 'filtered') return;
      this.expire('ack-timeout');
    }, this.ackTimeoutMs);
  }

  private clearAckTimer(): void {
    if (this.ackTimer != null) { this.timers.clearTimeout(this.ackTimer); this.ackTimer = null; }
  }

  private expire(reason: ChannelLinkExpiry): void {
    this.counters.expiries += 1;
    this.opts.log?.('channel-filter.fresh-socket', { channel: this.opts.channel, reason, timeoutMs: this.ackTimeoutMs });
    this.legacyNext = true;
    // Detach first: nothing this socket sends afterwards is admitted, even
    // if the owner's teardown were to deliver one more frame.
    this.closeSocket();
    this.opts.reconnect(reason);
  }

  private resetSocket(): void {
    this.socketEpoch += 1;
    this.fence.reset();
    this.installedScope = null;
    this.subscribed = false;
    this.outstanding = 0;
    this.clearAckTimer();
    if (this.pendingReplace != null) { this.timers.clearTimeout(this.pendingReplace); this.pendingReplace = null; }
    this.wireKey = null;
    this.lastSendMs = 0;
  }
}
